import { preferenceKey, restoreDocumentFilter } from './preferences';
import { documentReviewUrl } from './api';
import { DocumentReviewSettings } from './DocumentReviewSettings';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { clientApi } from '@/config/axios';
import { useAuth } from '@/lib/auth/AuthContext';
import { Button } from '@/components/ui/button';
import { DocumentClassificationPanel } from './DocumentClassificationPanel';
export function DocumentReviewQueue() {
  const { organization, user } = useAuth();
  const org = organization?.organization_id;
  const [phase, setPhase] = useState('');
  const [kind, setKind] = useState('');
  const [subject, setSubject] = useState('');
  const [unsigned, setUnsigned] = useState(false);
  const [offset, setOffset] = useState(0);
  const [sourceFilter, setSourceFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const taxonomy = useQuery({
    queryKey: ['document-review-settings', org],
    enabled: !!org,
    queryFn: async () =>
      (await clientApi.get<{ taxonomy: Record<string, string[]> }>(documentReviewUrl(org, '/settings'))).data,
  });
  const filterKey = preferenceKey(org, user?.id);
  const saveFilter = () =>
    localStorage.setItem(filterKey, JSON.stringify({ phase, kind, subject, unsigned, sourceFilter, dateFrom, dateTo }));
  const restoreFilter = () => {
    try {
      const f = restoreDocumentFilter(localStorage.getItem(filterKey));
      setPhase(typeof f.phase === 'string' ? f.phase : '');
      setKind(typeof f.kind === 'string' ? f.kind : '');
      setSubject(typeof f.subject === 'string' ? f.subject : '');
      setUnsigned(f.unsigned === true);
      setSourceFilter(typeof f.sourceFilter === 'string' ? f.sourceFilter : '');
      setDateFrom(typeof f.dateFrom === 'string' ? f.dateFrom : '');
      setDateTo(typeof f.dateTo === 'string' ? f.dateTo : '');
      setOffset(0);
    } catch {
      /* Invalid local preference is ignored. */
    }
  };
  const [selected, setSelected] = useState<{ id: string; document_kind: 'uploaded' | 'generated' } | null>(null);
  const query = useQuery({
    queryKey: ['classification-queue', org, phase, kind, subject, unsigned, offset, sourceFilter, dateFrom, dateTo],
    enabled: !!org,
    queryFn: async () =>
      (
        await clientApi.get<{
          documents: Array<{
            id: string;
            document_kind: 'uploaded' | 'generated';
            name: string;
            date: string;
            source: string;
            labels: Record<string, string>;
          }>;
          has_more: boolean;
        }>(documentReviewUrl(org), {
          params: {
            phase: phase || undefined,
            label_kind: kind || undefined,
            subject: subject || undefined,
            unsigned,
            source_filter: sourceFilter || undefined,
            date_from: dateFrom || undefined,
            date_to: dateTo || undefined,
            offset,
          },
        })
      ).data,
  });
  return (
    <details className="rounded border p-4">
      <summary className="cursor-pointer font-semibold">Document labels and review queue</summary>
      <div className="mt-3 space-y-3">
        <DocumentReviewSettings />
        <div className="flex gap-2">
          <Button variant="outline" onClick={saveFilter}>
            Save my filter on this browser
          </Button>
          <Button variant="outline" onClick={restoreFilter}>
            Restore my filter
          </Button>
        </div>
        <div className="flex flex-wrap gap-3">
          <label>
            Source
            <select
              value={sourceFilter}
              onChange={(e) => {
                setSourceFilter(e.target.value);
                setOffset(0);
              }}>
              <option value="">All</option>
              <option value="upload">Upload</option>
              <option value="srfax">Fax</option>
              <option value="generated">Generated</option>
            </select>
          </label>
          <label>
            From
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setOffset(0);
              }}
            />
          </label>
          <label>
            Through
            <input
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setOffset(0);
              }}
            />
          </label>
          {[
            [
              'Phase',
              phase,
              setPhase,
              ['Preconception', 'Prenatal', 'Intrapartum', 'Postpartum-Maternal', 'Newborn', 'Gyn/Well-woman', 'Other'],
            ],
            [
              'Kind',
              kind,
              setKind,
              [
                'Visit note',
                'Flowsheet export',
                'Lab result',
                'Imaging/Ultrasound report',
                'Consent',
                'Declination/Refusal',
                'Intake/History',
                'Birth summary',
                'Transfer/Hospital record',
                'Newborn screen',
                'Other',
              ],
            ],
            ['Subject', subject, setSubject, ['Mother', 'Newborn A', 'Newborn B', 'Both']],
          ].map(([name, value, set, options]) => (
            <label key={String(name)}>
              {String(name)}
              <select
                className="block rounded border p-2"
                value={value as string}
                onChange={(e) => {
                  (set as (v: string) => void)(e.target.value);
                  setOffset(0);
                }}>
                <option value="">All</option>
                {(taxonomy.data?.taxonomy[String(name).toLowerCase()] ?? (options as string[])).map((x) => (
                  <option key={x}>{x}</option>
                ))}
              </select>
            </label>
          ))}
          <label>
            <input
              type="checkbox"
              checked={unsigned}
              onChange={(e) => {
                setUnsigned(e.target.checked);
                setOffset(0);
              }}
            />{' '}
            Unsigned generated documents
          </label>
        </div>
        {query.isError && <p role="alert">Queue unavailable.</p>}
        {query.data?.documents.map((d) => (
          <button
            className="block w-full border-b p-2 text-left"
            key={`${d.document_kind}:${d.id}`}
            onClick={() => setSelected(d)}>
            {d.name} · {d.labels.phase || 'Unclassified'} · {d.labels.kind || d.source} ·{' '}
            {d.labels.subject || 'Subject not reviewed'}
          </button>
        ))}
        <div className="flex gap-2">
          <Button variant="outline" disabled={!offset} onClick={() => setOffset(Math.max(0, offset - 50))}>
            Previous
          </Button>
          <Button variant="outline" disabled={!query.data?.has_more} onClick={() => setOffset(offset + 50)}>
            Next
          </Button>
        </div>
        {selected && (
          <DocumentClassificationPanel
            key={`${org}:${selected.document_kind}:${selected.id}`}
            kind={selected.document_kind}
            documentId={selected.id}
          />
        )}
      </div>
    </details>
  );
}
